import { mapObjIndexed, pipe, sortBy, prop, assoc, when } from 'ramda';
import {
  TimetableItemProps,
  CompleteTimetableItemProps,
} from '../../../atoms/TimetableItem';
import { TimetableProps } from '../Timetable';
import { getHourDecimal } from '@versita/shared/timetable-utils';

export const useItemPositions = ({
  events: eventsByDay,
  rangeStart = 8,
  rangeEnd = 20,
}: Pick<TimetableProps, 'events' | 'rangeStart' | 'rangeEnd'>) => {
  return mapObjIndexed(
    pipe(sortBy(prop('start')), calculatePositions),
    eventsByDay,
  );

  /**
   * Calculate the vertical/horizontal positions and the height of each
   * timetable item along with the overlap groups
   */
  function calculatePositions(events: TimetableItemProps[]) {
    // Calculate overlap groups
    // (this method will only work if the events are sorted by start time)
    const { overlapGroups, eventsWithOverlapDetails } = events
      .sort((a, b) => Number(a.start > b.start))
      .reduce((acc, event, i) => {
        const isFirstIteration = () => i === 0;
        const isLastIteration = () => i === events.length - 1;
        const noOverlapDetected = () =>
          !isFirstIteration() && new Date(event.start) >= acc.currentGroup.end;

        const overlapGroupId =
          isFirstIteration() || !noOverlapDetected()
            ? String(Object.keys(acc.overlapGroups).length)
            : String(Object.keys(acc.overlapGroups).length + 1);
        const horizontalIndex =
          isFirstIteration() || noOverlapDetected()
            ? 0
            : acc.currentGroup.events.length;
        /**
         * The logic here is pretty complex.
         *
         * ## first iteration:
         *
         * impossible to overlap with anything yet,
         * just add this event to the current overlap group and move on.
         *
         * ## overlap detected:
         *
         * extend the end time of the current group if necessary and append the event
         * (start time won't ever need updating because we're moving through these
         * in chronological order)
         *
         * ## no overlap (and not first iteration)
         *
         * store the current group to the main object and
         * create a new current group based on the current event
         *
         * ## last iteration
         *
         * save the current group - we're done.
         */

        const currentGroup =
          isFirstIteration() || noOverlapDetected()
            ? createOverlapGroup(event, overlapGroupId, horizontalIndex)
            : appendToOverlapGroup(
                event,
                overlapGroupId,
                horizontalIndex,
                acc.currentGroup,
              );

        const overlapGroups = pipe(
          when(
            noOverlapDetected,
            assoc(
              String(parseInt(overlapGroupId) - 1),
              calculatePositionsForOverlapGroup(acc.currentGroup),
            ),
          ),
          when(
            isLastIteration,
            assoc(
              String(
                Object.keys(acc.overlapGroups).length +
                  (noOverlapDetected() ? 1 : 0),
              ),
              calculatePositionsForOverlapGroup(currentGroup),
            ),
          ),
        )(acc.overlapGroups) as typeof acc.overlapGroups;

        return {
          currentGroup,
          overlapGroups,
          eventsWithOverlapDetails: acc.eventsWithOverlapDetails.concat({
            ...event,
            overlapGroupId,
            horizontalIndex,
          }),
        };
      }, initialAccumulator());

    // Augment events with positions and overlap details
    const timetableEvents = eventsWithOverlapDetails.map((event) => {
      const topPositionPercent = calculateVerticalPos(
        event,
        rangeStart,
        rangeEnd,
      );
      const heightPercent = calculateItemHeight(event, rangeEnd - rangeStart);
      const overlapGroupSize =
        (event.overlapGroupId &&
          overlapGroups[event.overlapGroupId]?.events.length) ||
        0;
      return {
        ...event,
        overlaps: Math.max(overlapGroupSize - 1, 0),
        overlapGroupId: overlapGroupSize > 2 ? event.overlapGroupId : null,
        topPositionPercent,
        heightPercent,
      };
    });

    return {
      timetableEvents,
      overlapGroups,
    };
  }

  function createOverlapGroup(
    event: TimetableItemProps,
    overlapGroupId: string,
    horizontalIndex: number,
  ) {
    return {
      start: new Date(event.start),
      end: new Date(event.end),
      events: [
        {
          ...{ overlapGroupId, horizontalIndex },
          ...event,
        },
      ],
    };
  }
  function appendToOverlapGroup(
    event: TimetableItemProps,
    overlapGroupId: string,
    horizontalIndex: number,
    currentGroup: { start: Date; end: Date; events: TimetableItemProps[] },
  ) {
    return {
      start: currentGroup.start,
      end: new Date(
        Math.max(currentGroup.end.getTime(), new Date(event.end).getTime()),
      ),
      events: currentGroup.events.concat({
        ...{ overlapGroupId, horizontalIndex },
        ...event,
      }),
    };
  }

  function calculatePositionsForOverlapGroup(group: {
    start: Date;
    end: Date;
    events: TimetableItemProps[];
  }) {
    return {
      topPositionPercent: calculateVerticalPos(
        {
          start: group.start.toISOString(),
        },
        rangeStart,
        rangeEnd,
      ),
      heightPercent: calculateItemHeight(
        {
          start: group.start.toISOString(),
          end: group.end.toISOString(),
        },
        rangeEnd - rangeStart,
      ),
      start: group.start,
      end: group.end,
      events: group.events.map((event) => ({
        ...event,
        topPositionPercent: calculateVerticalPos(
          event,
          getHourDecimal(group.start.toISOString()),
          getHourDecimal(group.end.toISOString()),
        ),
        heightPercent: calculateItemHeight(
          event,
          getHourDecimal(group.end.toISOString()) -
            getHourDecimal(group.start.toISOString()),
        ),
        overlaps: 0,
      })),
    };
  }

  function calculateItemHeight(
    { start, end }: { start: string; end: string },
    timeRange: number,
  ) {
    const [startDecimal, endDecimal] = [
      getHourDecimal(start),
      getHourDecimal(end),
    ];
    return ((endDecimal - startDecimal) / timeRange) * 100;
  }

  function calculateVerticalPos(
    { start }: { start: string },
    rangeStart: number,
    rangeEnd: number,
  ) {
    const startDecimal = getHourDecimal(start);
    return ((startDecimal - rangeStart) / (rangeEnd - rangeStart)) * 100;
  }

  function initialAccumulator() {
    return {
      currentGroup: {
        start: new Date('1970'),
        end: new Date('1970'),
        events: [] as TimetableItemProps[],
      },
      overlapGroups: {} as Record<
        string,
        {
          events: CompleteTimetableItemProps[];
          topPositionPercent: number;
          heightPercent: number;
          start: Date;
          end: Date;
        }
      >,
      eventsWithOverlapDetails: [] as (TimetableItemProps &
        Pick<
          CompleteTimetableItemProps,
          'overlapGroupId' | 'horizontalIndex'
        >)[],
    };
  }
};
